import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';

import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminStudents from './pages/admin/AdminStudents';
import AdminStudentTests from './pages/admin/AdminStudentTests';
import AdminTestReview from './pages/admin/AdminTestReview';
import AdminQuestions from './pages/admin/AdminQuestions';
import AdminVideos from './pages/admin/AdminVideos';
import AdminManualReviews from './pages/admin/AdminManualReviews';
import AdminQuestionLookup from './pages/admin/AdminQuestionLookup';
import AdminCards from './pages/admin/AdminCards';
import StudentDashboard from './pages/student/StudentDashboard';
import TestBuilder from './pages/student/TestBuilder';
import TakeTest from './pages/student/TakeTest';
import TestResult from './pages/student/TestResult';
import TestHistory from './pages/student/TestHistory';
import VideosPage from './pages/student/VideosPage';
import ComingSoon from './pages/student/ComingSoon';

import AdminLayout from './components/shared/AdminLayout';
import StudentLayout from './components/shared/StudentLayout';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30000 } }
});

function ProtectedRoute({ children, role }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={!user ? <LoginPage /> : <Navigate to={user.role === 'admin' ? '/admin' : '/student'} />} />
      <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="students" element={<AdminStudents />} />
        <Route path="students/:studentId/tests" element={<AdminStudentTests />} />
        <Route path="questions" element={<AdminQuestions />} />
        <Route path="videos" element={<AdminVideos />} />
        <Route path="cards" element={<AdminCards />} />
        <Route path="manual-reviews" element={<AdminManualReviews />} />
        <Route path="question-lookup" element={<AdminQuestionLookup />} />
      </Route>
      <Route path="/admin/test-review/:testId" element={<ProtectedRoute role="admin"><AdminTestReview /></ProtectedRoute>} />
      <Route path="/student" element={<ProtectedRoute role="student"><StudentLayout /></ProtectedRoute>}>
        <Route index element={<StudentDashboard />} />
        <Route path="test/new" element={<TestBuilder />} />
        <Route path="test/:id" element={<TakeTest />} />
        <Route path="test/:id/result" element={<TestResult />} />
        <Route path="history" element={<TestHistory />} />
        <Route path="videos" element={<VideosPage />} />
        <Route path="cards" element={<ComingSoon />} />
      </Route>
      <Route path="/" element={<Navigate to={user ? (user.role === 'admin' ? '/admin' : '/student') : '/login'} />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
